import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppAuth } from "./AuthContext";
import {
    isFirebaseConfigured,
    firebaseSendPhoneOtp,
    firebaseConfirmPhoneOtp,
    resetRecaptchaVerifier,
    RECAPTCHA_CONTAINER_ID,
} from "./firebase";
import { normalizeE164, isValidPhone } from "../lib/phone";
import { AuthStage, PremiumAuthCard } from "./PremiumStage";
import EmailSignup from "./EmailSignup";

const DEV = process.env.NODE_ENV !== "production";
// Elegant, minimal shell: white base with the softest possible blush + lilac washes.
// No dominant pink. Depth comes from a subtle radial glow, not saturation.
const shellBg = "radial-gradient(60% 45% at 12% 8%, rgba(245,225,243,0.85) 0%, transparent 60%), radial-gradient(55% 45% at 92% 100%, rgba(228,220,240,0.75) 0%, transparent 65%), #FFFFFF";

// Softer, premium CTA — muted rose to muted lavender instead of hot pink → deep plum.
const CTA_GRADIENT = "linear-gradient(135deg,#D8A5C3 0%,#B49AD9 100%)";

const COUNTRIES = [
    { code: "+91",  flag: "🇮🇳", name: "India" },
    { code: "+1",   flag: "🇺🇸", name: "USA/Canada" },
    { code: "+44",  flag: "🇬🇧", name: "UK" },
    { code: "+61",  flag: "🇦🇺", name: "Australia" },
    { code: "+65",  flag: "🇸🇬", name: "Singapore" },
    { code: "+971", flag: "🇦🇪", name: "UAE" },
    { code: "+234", flag: "🇳🇬", name: "Nigeria" },
    { code: "+27",  flag: "🇿🇦", name: "South Africa" },
    { code: "+49",  flag: "🇩🇪", name: "Germany" },
    { code: "+33",  flag: "🇫🇷", name: "France" },
];

const AuthCard = PremiumAuthCard;
// eslint-disable-next-line no-unused-vars
function AuthCardLegacy({ children, title, subtitle, footer }) {
    return (
        <div className="w-full max-w-md rounded-[28px] bg-white border border-[#EEE6F3] shadow-[0_30px_80px_-40px_rgba(63,45,72,0.18)] p-8 md:p-10 relative" data-testid="auth-card">
            <Link to="/" aria-label="Close and return home" data-testid="auth-close-btn"
                className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-[#8677A0] hover:text-[#2A1F32] hover:bg-[#FBF7FB] transition">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
            </Link>
            <Link to="/" className="flex items-center justify-center gap-2 mb-6">
                <img src="/femcare-logo-v2.png" alt="Femcare.life" className="h-10 w-10 object-cover object-top rounded-xl ring-1 ring-[#EEE6F3]" />
                <span className="font-extrabold text-lg tracking-tight text-[#3F2D48]">
                    Femcare<span className="text-[#B49AD9]">.life</span>
                </span>
            </Link>
            <h1 className="text-[26px] leading-tight font-extrabold text-[#2A1F32] text-center tracking-tight">{title}</h1>
            {subtitle && <p className="text-[13.5px] text-[#6D5C7A] text-center mt-2 leading-relaxed">{subtitle}</p>}
            <div className="mt-7">{children}</div>
            {footer && <div className="mt-6 text-center text-sm text-[#6D5C7A]">{footer}</div>}
        </div>
    );
}

function FirebaseNotConfiguredCard() {
    return (
        <AuthCard
            title="Phone sign-in is being configured"
            subtitle="Please try again in a moment."
            footer={<a href="mailto:support@femcare.life" className="font-semibold text-[#8F639B] hover:text-[#6C4B7C]">Contact support</a>}>
            <div className="rounded-xl border border-[#EEE6F3] bg-[#FBF7FB] p-5 text-sm text-[#6D5C7A]" data-testid="fb-not-configured">
                Our secure phone sign-in provider is not yet enabled for this deployment. This is a one-time setup step by the site owner.
            </div>
        </AuthCard>
    );
}

function PhoneAuth({ mode = "login" }) {
    const { firebasePhoneVerify, login } = useAppAuth();
    const nav = useNavigate();
    const configured = isFirebaseConfigured();
    const isSignup = mode === "signup";

    // Mobile-parity signup fields (only used when isSignup) — matches
    // the current FT/mobile signup screen exactly:
    //   Full name * · Email * · Password (min 6) · Date of Birth (DD-MM-YYYY)
    const [fullName, setFullName] = useState("");
    const [email,    setEmail]    = useState("");
    const [password, setPassword] = useState("");
    const [dob,      setDob]      = useState("");   // stored as DD-MM-YYYY string

    // Login-mode "sign in with email + password" tab. Defaults to Email/Password
    // (mobile primary) but the user can flip to Phone OTP.
    const [loginMode, setLoginMode] = useState("email"); // 'email' | 'phone'

    const [cc, setCc]     = useState("+91");
    const [num, setNum]   = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [otpError, setOtpError] = useState(null);
    const [resendIn, setResendIn] = useState(0);
    const [otpSent, setOtpSent] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verified, setVerified] = useState(false);
    const confirmationRef = useRef(null);
    const idTokenRef = useRef(null);

    const phone = useMemo(() => normalizeE164(cc, num) || `${cc}${num.replace(/[^\d]/g,"")}`, [cc, num]);
    const canSendPhone = isValidPhone(cc, num);
    const derivedCountry = useMemo(() => COUNTRIES.find(c => c.code === cc) || COUNTRIES[0], [cc]);

    // Signup field validation — mirrors current mobile parity (Full name required,
    // valid email, 6+ char password, valid DOB with ≥13yr age gate).
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    const dobValid   = /^\d{2}-\d{2}-\d{4}$/.test(dob) && (() => {
        const [d, m, y] = dob.split("-").map(Number);
        if (!d || !m || !y) return false;
        const dt = new Date(y, m - 1, d);
        if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return false;
        const ageYears = (Date.now() - dt.getTime()) / (365.25 * 24 * 3600 * 1000);
        return ageYears >= 13 && ageYears < 110;
    })();
    const signupFormValid = fullName.trim().length >= 1 && emailValid && password.length >= 6 && dobValid && canSendPhone;
    const canProceedToOtp = isSignup ? signupFormValid : canSendPhone;

    const [digits, setDigits] = useState(["","","","","",""]);
    const boxRefs = useRef([]);
    const code = digits.join("");
    const canVerify = code.length === 6 && /^\d{6}$/.test(code);

    // Resend cooldown
    useEffect(() => {
        if (!otpSent) return;
        const t = setInterval(() => setResendIn(v => v > 0 ? v - 1 : 0), 1000);
        return () => clearInterval(t);
    }, [otpSent]);

    // Clean up ONCE when the user actually navigates away from the auth surface.
    useEffect(() => {
        return () => { resetRecaptchaVerifier(); };
    }, []);

    const showFriendlyError = useCallback((e) => {
        const errCode = e?.code || "";
        if (DEV) console.debug("[fc/auth] phone error", errCode, e?.message);
        const map = {
"auth/billing-not-enabled": "Phone OTP is temporarily unavailable. Please sign in with your email & password.",
"auth/operation-not-allowed": "Phone OTP is temporarily unavailable. Please sign in with your email & password.",
"auth/internal-error": "Phone OTP is temporarily unavailable. Please sign in with your email & password.",
            "auth/invalid-phone-number":      "That number doesn't look right. Please recheck.",
            "auth/too-many-requests":         "Too many attempts. Please wait a few minutes.",
            "auth/quota-exceeded":            "SMS quota temporarily exceeded. Please try again shortly.",
            "auth/captcha-check-failed":      "We couldn't start phone verification. Please try again.",
            "auth/invalid-app-credential":    "We couldn't start phone verification. Please try again.",
            "auth/invalid-verification-code": "Invalid OTP. Please try again.",
            "auth/code-expired":              "Codes expire 10 minutes after they're created. Tap Resend for a new one.",
            "auth/network-request-failed":    "Network issue. Please recheck your connection.",
            "femcare/recaptcha-container-missing": "We couldn't start phone verification. Please try again.",
            "femcare/firebase-not-configured":     "Phone sign-in is temporarily unavailable.",
        };
        return map[errCode] || "Something went wrong. Please try again.";
    }, []);

    const sendOtp = useCallback(async () => {
        setError(null); setOtpError(null); setBusy(true);
        setVerified(false); idTokenRef.current = null;
        try {
            confirmationRef.current = await firebaseSendPhoneOtp(phone);
            setResendIn(30);
            setDigits(["","","","","",""]);
            setOtpSent(true);
            setTimeout(() => boxRefs.current[0]?.focus(), 50);
        } catch (e) {
            setError(showFriendlyError(e));
        } finally { setBusy(false); }
    }, [phone, showFriendlyError]);

    // Auto-verify the OTP as soon as the user enters 6 digits — Firebase only,
    // does NOT create the session yet. We cache the idToken for the finalize step.
    const verifyOtpCode = useCallback(async () => {
        setOtpError(null); setVerifying(true);
        try {
            const idToken = await firebaseConfirmPhoneOtp(confirmationRef.current, code);
            idTokenRef.current = idToken;
            setVerified(true);
        } catch (e) {
            setVerified(false); idTokenRef.current = null;
            setOtpError(showFriendlyError(e));
        } finally { setVerifying(false); }
    }, [code, showFriendlyError]);

    useEffect(() => {
        if (otpSent && canVerify && !verifying && !verified && !otpError && !busy) {
            verifyOtpCode();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canVerify, otpSent]);

    // Finalize — send cached Firebase idToken to backend, create fc_sid, redirect.
    // For signup, we also pass full_name/email/password/dob so the canonical FT
    // profile is enriched with mobile-parity attributes immediately after auth.
    const finalize = useCallback(async () => {
        if (!verified || !idTokenRef.current) return;
        setError(null); setBusy(true);
        try {
            const extras = isSignup ? {
                full_name: fullName.trim(),
                email: email.trim(),
                password: password,
                dob: dob,
            } : undefined;
            await firebasePhoneVerify(idTokenRef.current, extras);
            nav("/app/home");
        } catch (e) {
            setError(showFriendlyError(e));
        } finally { setBusy(false); }
    }, [verified, firebasePhoneVerify, nav, showFriendlyError, isSignup, fullName, email, password, dob]);

    const tryAgain = useCallback(() => {
        setOtpError(null); setVerified(false); idTokenRef.current = null;
        setDigits(["","","","","",""]);
        setTimeout(() => boxRefs.current[0]?.focus(), 30);
        // Resend a fresh OTP so the user isn't stuck on an expired one
        sendOtp();
    }, [sendOtp]);

    const changeNumber = useCallback(() => {
        setOtpSent(false); setVerified(false); setVerifying(false);
        setOtpError(null); setError(null);
        setDigits(["","","","","",""]);
        idTokenRef.current = null;
    }, []);

    const setDigitAt = (i, v) => {
        const clean = v.replace(/[^\d]/g,"").slice(0,1);
        setDigits(d => { const n = [...d]; n[i] = clean; return n; });
        // Clear stale error/verified state as soon as the user starts typing again
        if (otpError) setOtpError(null);
        if (verified) { setVerified(false); idTokenRef.current = null; }
        if (clean && i < 5) boxRefs.current[i+1]?.focus();
    };
    const onKeyDown = (i) => (e) => {
        if (e.key === "Backspace" && !digits[i] && i > 0) boxRefs.current[i-1]?.focus();
        if (e.key === "ArrowLeft"  && i > 0) boxRefs.current[i-1]?.focus();
        if (e.key === "ArrowRight" && i < 5) boxRefs.current[i+1]?.focus();
        if (e.key === "Enter" && verified && !busy) finalize();
    };
    const onPaste = (e) => {
        const t = (e.clipboardData?.getData("text") || "").replace(/[^\d]/g,"").slice(0,6);
        if (!t) return;
        e.preventDefault();
        setOtpError(null); setVerified(false); idTokenRef.current = null;
        const next = t.padEnd(6," ").split("").map(c => c === " " ? "" : c);
        setDigits(next);
        const focusIdx = Math.min(t.length, 5);
        setTimeout(() => boxRefs.current[focusIdx]?.focus(), 20);
    };

    // Email + Password login (mobile-primary path). Declared here so React hook
    // order stays stable regardless of the `configured` / signup-vs-login branches.
    const submitEmailLogin = useCallback(async (e) => {
        e.preventDefault();
        setError(null); setBusy(true);
        try {
            await login(email.trim(), password);
            nav("/app/home");
        } catch (err) {
            setError((err && err.message) || "Invalid email or password");
        } finally { setBusy(false); }
    }, [login, email, password, nav]);

    if (!configured) {
        return (
            <div className="fc-auth-root" data-mode={isSignup ? "signup" : "login"}><AuthStage isSignup={isSignup} />
                <FirebaseNotConfiguredCard />
                {/* reCAPTCHA container is intentionally omitted in the not-configured branch */}
            </div>
        );
    }

    // Email + Password login path (login mode only, phone tab NOT selected)
    if (!isSignup && loginMode === "email") {
        return (
            <div className="fc-auth-root" data-mode={isSignup ? "signup" : "login"}><AuthStage isSignup={isSignup} />
                <AuthCard
                    title="Sign in to Femcare"
                    subtitle="Same account across mobile & web — one identity, one Femcare."
                    footer={
                        <div className="space-y-2">
                            <div>Forgot your password? <Link to="/app/forgot-password" className="font-semibold text-[#8F639B] hover:text-[#6C4B7C] hover:underline underline-offset-2" data-testid="link-to-forgot">Reset it</Link></div>
                            <div data-testid="auth-cross-link">
                                New here? <Link to="/app/signup" className="font-semibold text-[#8F639B] hover:text-[#6C4B7C] hover:underline underline-offset-2" data-testid="link-to-signup">Sign up</Link>
                            </div>
                        </div>
                    }>
                    <div className="grid grid-cols-2 gap-1 rounded-xl bg-[#FBF7FB] p-1 mb-5" data-testid="login-mode-tabs">
                        <button type="button" onClick={()=>setLoginMode("email")}
                            className="rounded-lg px-3 py-2 text-xs font-bold text-white shadow" style={{background: CTA_GRADIENT}}
                            data-testid="tab-email">Email &amp; Password</button>
                        <button type="button" onClick={()=>setLoginMode("phone")}
                            className="rounded-lg px-3 py-2 text-xs font-bold text-[#6D5C7A] hover:text-[#2A1F32]"
                            data-testid="tab-phone">Phone OTP</button>
                    </div>
                    <form onSubmit={submitEmailLogin} className="space-y-4" data-testid="email-login-form">
                        <label className="block">
                            <span className="text-[11px] font-bold text-[#6D5C7A] uppercase tracking-[0.14em]">Email</span>
                            <input type="email" required data-testid="login-email"
                                value={email} onChange={(e)=>setEmail(e.target.value)}
                                autoComplete="email" placeholder="you@example.com"
                                className="mt-2 w-full rounded-xl border border-[#EEE6F3] bg-white px-4 py-3 text-[#2A1F32] placeholder:text-[#B7A9C3] focus:ring-2 focus:ring-[#D8C4E7] focus:border-[#B49AD9] focus:outline-none" />
                        </label>
                        <label className="block">
                            <span className="text-[11px] font-bold text-[#6D5C7A] uppercase tracking-[0.14em]">Password</span>
                            <input type="password" required data-testid="login-password"
                                value={password} onChange={(e)=>setPassword(e.target.value)}
                                autoComplete="current-password" minLength={6}
                                className="mt-2 w-full rounded-xl border border-[#EEE6F3] bg-white px-4 py-3 text-[#2A1F32] placeholder:text-[#B7A9C3] focus:ring-2 focus:ring-[#D8C4E7] focus:border-[#B49AD9] focus:outline-none" />
                        </label>
                        {error && <p className="text-sm text-[#B04A6A]" data-testid="login-error">{error}</p>}
                        <button type="submit" disabled={busy || !email || !password} data-testid="login-submit"
                            className="w-full rounded-xl px-6 py-3.5 text-white font-semibold shadow-[0_10px_25px_-10px_rgba(180,154,217,0.7)] hover:shadow-[0_14px_28px_-12px_rgba(180,154,217,0.85)] hover:-translate-y-0.5 transition disabled:opacity-50"
                            style={{background: CTA_GRADIENT}}>
                            {busy ? "Signing in…" : "Sign in"}
                        </button>
                        <p className="text-[11px] text-[#8677A0] text-center pt-1">
                            By continuing, you agree to our <Link to="/terms" className="text-[#8F639B] font-semibold hover:underline">Terms</Link> &amp; <Link to="/privacy" className="text-[#8F639B] font-semibold hover:underline">Privacy Policy</Link>.
                        </p>
                    </form>
                </AuthCard>
                <div id={RECAPTCHA_CONTAINER_ID} aria-hidden="true" />
            </div>
        );
    }

    const primaryLabel = isSignup ? "Create account" : "Sign in";

    return (
        <div className="fc-auth-root" data-mode={isSignup ? "signup" : "login"}><AuthStage isSignup={isSignup} />
            <AuthCard
                title={isSignup ? "Create your Femcare account" : "Sign in with your phone"}
                subtitle="Same account across mobile & web — one identity, one Femcare."
                footer={
                    <div className="space-y-2">
                        <div>Trouble receiving the code? <a href="mailto:support@femcare.life" className="font-semibold text-[#8F639B] hover:text-[#6C4B7C]">Contact support</a></div>
                        <div data-testid="auth-cross-link">
                            {isSignup ? (
                                <>Already have an account? <Link to="/app/login" className="font-semibold text-[#8F639B] hover:text-[#6C4B7C] hover:underline underline-offset-2" data-testid="link-to-login">Log in</Link></>
                            ) : (
                                <>New here? <Link to="/app/signup" className="font-semibold text-[#8F639B] hover:text-[#6C4B7C] hover:underline underline-offset-2" data-testid="link-to-signup">Sign up</Link></>
                            )}
                        </div>
                    </div>
                }>
                {!isSignup && (
                    <div className="grid grid-cols-2 gap-1 rounded-xl bg-[#FBF7FB] p-1 mb-5" data-testid="login-mode-tabs">
                        <button type="button" onClick={()=>setLoginMode("email")}
                            className="rounded-lg px-3 py-2 text-xs font-bold text-[#6D5C7A] hover:text-[#2A1F32]"
                            data-testid="tab-email">Email &amp; Password</button>
                        <button type="button" onClick={()=>setLoginMode("phone")}
                            className="rounded-lg px-3 py-2 text-xs font-bold text-white shadow" style={{background: CTA_GRADIENT}}
                            data-testid="tab-phone">Phone OTP</button>
                    </div>
                )}
                <form onSubmit={(e)=>{e.preventDefault();
                        if (verified && !busy) finalize();
                        else if (!otpSent && canProceedToOtp && !busy) sendOtp();
                    }} className="space-y-4" data-testid="phone-form">
                    {isSignup && !otpSent && (
                        <>
                            <label className="block">
                                <span className="text-[11px] font-bold text-[#6D5C7A] uppercase tracking-[0.14em]">Full name *</span>
                                <input type="text" required data-testid="signup-full-name"
                                    value={fullName} onChange={(e)=>setFullName(e.target.value)}
                                    placeholder="Your full name" maxLength={120}
                                    className="mt-2 w-full rounded-xl border border-[#EEE6F3] bg-white px-4 py-3 text-[#2A1F32] placeholder:text-[#B7A9C3] focus:ring-2 focus:ring-[#D8C4E7] focus:border-[#B49AD9] focus:outline-none" />
                            </label>
                            <label className="block">
                                <span className="text-[11px] font-bold text-[#6D5C7A] uppercase tracking-[0.14em]">Email *</span>
                                <input type="email" required data-testid="signup-email"
                                    value={email} onChange={(e)=>setEmail(e.target.value)}
                                    autoComplete="email" placeholder="you@example.com"
                                    className={`mt-2 w-full rounded-xl border bg-white px-4 py-3 text-[#2A1F32] placeholder:text-[#B7A9C3] focus:ring-2 focus:outline-none ${email && !emailValid ? "border-[#D66E9F] focus:border-[#D66E9F] focus:ring-[#F5DAE8]" : "border-[#EEE6F3] focus:border-[#B49AD9] focus:ring-[#D8C4E7]"}`} />
                                {email && !emailValid && <p className="text-[11px] text-[#B04A6A] mt-1">Please enter a valid email address</p>}
                            </label>
                            <label className="block">
                                <span className="text-[11px] font-bold text-[#6D5C7A] uppercase tracking-[0.14em]">Password *</span>
                                <input type="password" required data-testid="signup-password"
                                    value={password} onChange={(e)=>setPassword(e.target.value)}
                                    autoComplete="new-password" placeholder="Minimum 6 characters" minLength={6}
                                    className="mt-2 w-full rounded-xl border border-[#EEE6F3] bg-white px-4 py-3 text-[#2A1F32] placeholder:text-[#B7A9C3] focus:ring-2 focus:ring-[#D8C4E7] focus:border-[#B49AD9] focus:outline-none" />
                                {password && password.length < 6 && <p className="text-[11px] text-[#B04A6A] mt-1">Password must be at least 6 characters</p>}
                            </label>
                            <label className="block">
                                <span className="text-[11px] font-bold text-[#6D5C7A] uppercase tracking-[0.14em]">Date of Birth (DD-MM-YYYY)</span>
                                <input type="text" required data-testid="signup-dob"
                                    value={dob}
                                    onChange={(e)=>{
                                        let raw = e.target.value.replace(/[^\d]/g,"").slice(0,8);
                                        let out = raw;
                                        if (raw.length > 4) out = `${raw.slice(0,2)}-${raw.slice(2,4)}-${raw.slice(4)}`;
                                        else if (raw.length > 2) out = `${raw.slice(0,2)}-${raw.slice(2)}`;
                                        setDob(out);
                                    }}
                                    placeholder="DD-MM-YYYY" inputMode="numeric" maxLength={10}
                                    className={`mt-2 w-full rounded-xl border bg-white px-4 py-3 text-[#2A1F32] placeholder:text-[#B7A9C3] focus:ring-2 focus:outline-none ${dob && !dobValid ? "border-[#D66E9F] focus:border-[#D66E9F] focus:ring-[#F5DAE8]" : "border-[#EEE6F3] focus:border-[#B49AD9] focus:ring-[#D8C4E7]"}`} />
                                {dob && !dobValid && <p className="text-[11px] text-[#B04A6A] mt-1">Please enter a valid date of birth (you must be at least 13)</p>}
                            </label>
                        </>
                    )}
                    <label className="block">
                        <span className="text-[11px] font-bold text-[#6D5C7A] uppercase tracking-[0.14em]">Mobile number</span>
                        <div className="mt-2 flex gap-2">
                            <select value={cc} onChange={(e)=>setCc(e.target.value)} data-testid="phone-country"
                                disabled={otpSent}
                                className="rounded-xl border border-[#EEE6F3] bg-white px-2.5 py-3 text-sm font-semibold text-[#2A1F32] focus:ring-2 focus:ring-[#D8C4E7] focus:border-[#B49AD9] focus:outline-none disabled:bg-[#FBF7FB]">
                                {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                            </select>
                            <input type="tel" inputMode="numeric" required placeholder="98765 43210"
                                value={num} onChange={(e)=>setNum(e.target.value)}
                                disabled={otpSent}
                                autoComplete="tel-national" autoFocus data-testid="phone-number"
                                className="flex-1 rounded-xl border border-[#EEE6F3] bg-white px-4 py-3 text-[#2A1F32] placeholder:text-[#B7A9C3] focus:ring-2 focus:ring-[#D8C4E7] focus:border-[#B49AD9] focus:outline-none disabled:bg-[#FBF7FB] disabled:text-[#6D5C7A]" />
                        </div>
                        {otpSent && (
                            <button type="button" onClick={changeNumber} data-testid="phone-change"
                                className="mt-2 text-[11px] font-semibold text-[#8F639B] hover:text-[#6C4B7C] hover:underline underline-offset-2">
                                ← Change number
                            </button>
                        )}
                    </label>

                    {isSignup && !otpSent && (
                        <>
                            <div data-testid="signup-country-chip" className="flex items-center justify-between rounded-xl border border-[#EEE6F3] bg-[#FBF7FB] px-4 py-3">
                                <span className="flex items-center gap-2 text-sm">
                                    <span aria-hidden="true" className="text-[#8F639B]">🌐</span>
                                    <span className="text-lg leading-none">{derivedCountry.flag}</span>
                                    <span className="font-semibold text-[#2A1F32]" data-testid="signup-country-name">{derivedCountry.name}</span>
                                </span>
                                <span aria-label="Locked, derived from your phone country code" className="text-[#B49AD9]">🔒</span>
                            </div>
                            <p className="text-[11px] text-[#8677A0] -mt-2 pl-1">
                                Country is set from your mobile country code. You can change this later in settings.
                            </p>
                            <div data-testid="signup-privacy-card" className="rounded-xl border border-[#EEE6F3] bg-gradient-to-br from-[#FBF7FB] to-[#F7F1F9] p-4">
                                <p className="text-sm font-bold text-[#2A1F32] flex items-center gap-2">
                                    <span aria-hidden="true" className="text-[#B49AD9]">🔒</span>
                                    Your cycle data stays private and protected.
                                </p>
                                <p className="text-xs text-[#6D5C7A] mt-1.5 leading-relaxed">
                                    Securely stored with your Femcare account and used to provide your predictions and personalised insights.
                                </p>
                            </div>
                        </>
                    )}

                    {error && <p className="text-sm text-[#B04A6A]" data-testid="phone-error">{error}</p>}

                    {/* Send-OTP button before the OTP has been sent — mobile parity CTA */}
                    {!otpSent && (() => {
                        const ready = canProceedToOtp && !busy;
                        return (
                            <button type="submit" disabled={!ready} data-testid="phone-send-otp"
                                className={`w-full rounded-xl px-6 py-3.5 text-white font-semibold transition disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed
                                    ${ready
                                        ? "shadow-[0_18px_40px_-12px_rgba(214,77,140,0.75)] -translate-y-0.5 animate-pulse hover:animate-none hover:shadow-[0_22px_50px_-14px_rgba(214,77,140,0.9)] brightness-110"
                                        : "shadow-[0_10px_25px_-10px_rgba(180,154,217,0.7)]"}`}
                                style={{background: ready ? "linear-gradient(135deg,#EC4899 0%,#D64D8C 55%,#8F638D 100%)" : CTA_GRADIENT}}>
                                {busy ? "Sending…" : (isSignup ? "Verify & Create Account" : "Send OTP")}
                            </button>
                        );
                    })()}

                    {/* Inline OTP section — appears after Send OTP succeeds */}
                    {otpSent && (
                        <div className="space-y-3" data-testid="otp-section">
                            <div className="rounded-xl bg-[#F1FCF3] border border-[#B7E7C0] px-4 py-2.5 text-sm text-[#1F6B39]" data-testid="otp-sent-msg">
                                We've sent a 6-digit code to <b className="text-[#134421]">{phone}</b>
                            </div>
                            <label className="block">
                                <span className="text-[11px] font-bold text-[#6D5C7A] uppercase tracking-[0.14em]">Enter 6-digit code</span>
                                <fieldset className="mt-2">
                                    <legend className="sr-only">One-time passcode</legend>
                                    <div className="flex justify-between gap-2" onPaste={onPaste}>
                                        {digits.map((d, i) => (
                                            <input key={i} ref={el => boxRefs.current[i]=el}
                                                type="text" inputMode="numeric" maxLength={1}
                                                aria-label={`Digit ${i+1}`}
                                                value={d}
                                                onChange={(e)=>setDigitAt(i, e.target.value)}
                                                onKeyDown={onKeyDown(i)}
                                                disabled={verifying || verified}
                                                data-testid={`otp-digit-${i}`}
                                                className={`w-12 h-14 md:w-14 md:h-16 text-center text-xl md:text-2xl font-extrabold rounded-xl border bg-white text-[#2A1F32] focus:ring-2 focus:outline-none transition
                                                    ${verified ? "border-[#3EA260] ring-[#B7E7C0]" : otpError ? "border-[#D66E9F] ring-[#F5DAE8]" : "border-[#EEE6F3] focus:border-[#B49AD9] focus:ring-[#D8C4E7]"}
                                                    disabled:cursor-default`} />
                                        ))}
                                    </div>
                                </fieldset>
                            </label>

                            {/* Status indicators */}
                            {verifying && (
                                <div className="flex items-center gap-2 text-sm text-[#6D5C7A]" data-testid="otp-verifying">
                                    <span className="w-2 h-2 bg-[#B49AD9] rounded-full animate-pulse" />
                                    Verifying…
                                </div>
                            )}
                            {verified && (
                                <div className="inline-flex items-center gap-2 rounded-full bg-[#DEF3E4] text-[#134421] px-3 py-1.5 text-xs font-bold" data-testid="otp-verified">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                    Verified
                                </div>
                            )}
                            {otpError && !verifying && !verified && (
                                <div className="flex items-center justify-between text-sm" data-testid="otp-error">
                                    <span className="text-[#B04A6A]">{otpError}</span>
                                    <button type="button" onClick={tryAgain} disabled={busy}
                                        className="font-semibold text-[#8F639B] hover:text-[#6C4B7C] hover:underline underline-offset-2 disabled:opacity-50"
                                        data-testid="otp-try-again">
                                        Try again →
                                    </button>
                                </div>
                            )}

                            {/* Resend + change controls */}
                            {!verified && !otpError && (
                                <div className="flex justify-end items-center text-xs">
                                    {resendIn > 0
                                        ? <span className="text-[#8677A0]" data-testid="otp-resend-timer">Resend in {resendIn}s</span>
                                        : <button type="button" onClick={sendOtp} disabled={busy} data-testid="otp-resend" className="font-semibold text-[#8F639B] hover:text-[#6C4B7C] hover:underline underline-offset-2">Resend OTP</button>
                                    }
                                </div>
                            )}

                            {/* Primary action — only enabled + highlighted after Firebase confirms */}
                            <button type="submit" disabled={!verified || busy}
                                data-testid="phone-finalize"
                                className={`w-full rounded-xl px-6 py-3.5 font-semibold transition
                                    ${verified
                                        ? "text-white shadow-[0_14px_30px_-8px_rgba(180,154,217,0.85)] hover:-translate-y-0.5 hover:shadow-[0_18px_36px_-8px_rgba(180,154,217,1)]"
                                        : "text-[#8677A0] bg-[#F3EDF5] cursor-not-allowed"}
                                    disabled:opacity-70`}
                                style={verified ? {background: CTA_GRADIENT} : undefined}>
                                {busy ? (isSignup ? "Creating account…" : "Signing in…") : primaryLabel}
                            </button>
                        </div>
                    )}

                    {isSignup && !otpSent && (
                        <>
                            <div className="relative py-1" data-testid="signup-login-divider">
                                <div className="absolute inset-0 flex items-center" aria-hidden>
                                    <div className="w-full border-t border-[#EEE6F3]" />
                                </div>
                                <div className="relative flex justify-center">
                                    <span className="px-3 bg-white text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8677A0]">Already with us?</span>
                                </div>
                            </div>
                            <Link to="/app/login" data-testid="signup-login-btn"
                                className="w-full inline-flex items-center justify-center rounded-xl px-6 py-3 text-[#6C4B7C] font-semibold border border-[#EEE6F3] bg-white hover:border-[#B49AD9] hover:bg-[#FBF7FB] transition">
                                Sign-In
                            </Link>
                        </>
                    )}

                    <p className="text-[11px] text-[#8677A0] text-center pt-1">
                        By continuing, you agree to our <Link to="/terms" className="text-[#8F639B] font-semibold hover:underline">Terms</Link> & <Link to="/privacy" className="text-[#8F639B] font-semibold hover:underline">Privacy Policy</Link>.
                    </p>
                </form>
            </AuthCard>

            {/* Stable reCAPTCHA container — mounted EXACTLY once at PhoneAuth root */}
            <div id={RECAPTCHA_CONTAINER_ID} aria-hidden="true" />
        </div>
    );
}

export const LoginPage  = () => <PhoneAuth mode="login" />;
export const SignupPage = () => <EmailSignup />;
